<template>
  <v-card flat>
    <v-card-text>
      <!-- Options -->
      <v-row class="mb-2" align="center">
        <v-col cols="auto">
          <v-switch
            v-model="onlyWithMovement"
            label="Only show items with movement"
            dense
            hide-details
            class="mt-0"
          />
        </v-col>
      </v-row>

      <!-- Summary Cards -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="2">
          <v-card color="blue darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.total_item_count }}
            </div>
            <div class="caption">Items</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="2">
          <v-card color="red darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.totals.total_sold }}
            </div>
            <div class="caption">Total Sold</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="2">
          <v-card color="orange darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.totals.total_consumed }}
            </div>
            <div class="caption">Total Consumed</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="2">
          <v-card color="purple darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.totals.total_transferred_out }}
            </div>
            <div class="caption">Transferred Out</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="2">
          <v-card color="teal darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.totals.total_transferred_in }}
            </div>
            <div class="caption">Transferred In</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="2">
          <v-card color="amber darken-2" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.totals.total_returned }}
            </div>
            <div class="caption">Returned</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Period Info -->
      <v-alert v-if="report" type="info" dense text class="mb-4">
        Stock movement for period: <strong>{{ report.from_date }}</strong> to
        <strong>{{ report.to_date }}</strong>
      </v-alert>

      <!-- Data Table -->
      <v-data-table
        v-if="report"
        :items="flatItems"
        :headers="headers"
        :search="search"
        item-key="id"
        dense
        group-by="item_type"
        :items-per-page="50"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search items..."
            dense
            class="mb-2"
          />
        </template>
        <template v-slot:group.header="{ group, headers, toggle, isOpen }">
          <td
            :colspan="headers.length"
            class="primary white--text pa-2 font-weight-bold"
            style="cursor: pointer"
            @click="toggle"
          >
            <v-icon dark small class="mr-2">{{
              isOpen ? "mdi-minus" : "mdi-plus"
            }}</v-icon>
            {{ group || "Uncategorized" }}
            <span class="float-right mr-4">
              Sold: {{ getGroupTotal(group, "sold") }} | Consumed:
              {{ getGroupTotal(group, "consumed") }}
            </span>
          </td>
        </template>
        <template v-slot:item.opening_stock="{ item }">
          <span class="font-weight-medium">{{ item.opening_stock }}</span>
        </template>
        <template v-slot:item.sold="{ item }">
          <span :class="item.sold > 0 ? 'red--text font-weight-bold' : ''">
            {{ item.sold > 0 ? "-" + item.sold : "0" }}
          </span>
        </template>
        <template v-slot:item.consumed="{ item }">
          <span
            :class="item.consumed > 0 ? 'orange--text font-weight-bold' : ''"
          >
            {{ item.consumed > 0 ? "-" + item.consumed : "0" }}
          </span>
        </template>
        <template v-slot:item.transferred_out="{ item }">
          <span
            :class="
              item.transferred_out > 0 ? 'purple--text font-weight-bold' : ''
            "
          >
            {{ item.transferred_out > 0 ? "-" + item.transferred_out : "0" }}
          </span>
        </template>
        <template v-slot:item.transferred_in="{ item }">
          <span
            :class="
              item.transferred_in > 0 ? 'teal--text font-weight-bold' : ''
            "
          >
            {{ item.transferred_in > 0 ? "+" + item.transferred_in : "0" }}
          </span>
        </template>
        <template v-slot:item.returned="{ item }">
          <span
            :class="
              item.returned > 0
                ? 'amber--text text--darken-3 font-weight-bold'
                : ''
            "
          >
            {{ item.returned > 0 ? "-" + item.returned : "0" }}
          </span>
        </template>
        <template v-slot:item.closing_stock="{ item }">
          <v-chip :color="item.closing_stock <= 0 ? 'red' : 'green'" dark small>
            {{ item.closing_stock }}
          </v-chip>
        </template>
        <template v-slot:item.net_change="{ item }">
          <span
            :class="
              item.net_change < 0
                ? 'red--text font-weight-bold'
                : item.net_change > 0
                ? 'green--text font-weight-bold'
                : ''
            "
          >
            {{ item.net_change > 0 ? "+" + item.net_change : item.net_change }}
          </span>
        </template>
      </v-data-table>

      <!-- Empty State -->
      <div v-if="!report" class="text-center grey--text pa-10">
        <v-icon size="64" color="grey lighten-1">mdi-swap-horizontal</v-icon>
        <p class="mt-4 subtitle-1">
          Click "Generate" to see a full stock movement reconciliation.
        </p>
        <p class="caption grey--text">
          Tracks sold, consumed, transferred, and returned quantities per item.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "StockMovementReport",
  props: {
    filters: { type: Object, required: true },
  },
  data() {
    return {
      report: null,
      search: "",
      onlyWithMovement: true,
      headers: [
        { text: "Item Name", value: "item_name" },
        { text: "Supplier", value: "supplier" },
        { text: "Batch", value: "batch_name" },
        { text: "Opening", value: "opening_stock", align: "center" },
        { text: "Sold", value: "sold", align: "center" },
        { text: "Consumed", value: "consumed", align: "center" },
        { text: "Xfer Out", value: "transferred_out", align: "center" },
        { text: "Xfer In", value: "transferred_in", align: "center" },
        { text: "Returned", value: "returned", align: "center" },
        { text: "Closing", value: "closing_stock", align: "center" },
        { text: "Net Change", value: "net_change", align: "center" },
      ],
    };
  },
  computed: {
    flatItems() {
      if (!this.report || !this.report.groups) return [];
      let items = [];
      this.report.groups.forEach((group) => {
        group.items.forEach((item) => {
          items.push({ ...item, item_type: group.item_type });
        });
      });
      return items;
    },
  },
  methods: {
    ...mapActions({
      fetchReport: "inventory_reports/get_stock_movement_report",
    }),
    getGroupTotal(groupName, field) {
      if (!this.report || !this.report.groups) return 0;
      let group = this.report.groups.find((g) => g.item_type === groupName);
      if (!group) return 0;
      return group["subtotal_" + field] || 0;
    },
    async generate() {
      try {
        let params = {};
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;
        if (this.filters.item_type_id)
          params.item_type_id = this.filters.item_type_id;
        if (this.filters.supplier) params.supplier = this.filters.supplier;
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        if (this.onlyWithMovement) params.only_with_movement = "1";
        this.report = await this.fetchReport(params);
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error generating stock movement report: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>
